import { AuthenticationDetails } from "amazon-cognito-identity-js";
import React, { useState } from "react";
import userPool from "../../../utils/userPool";
import { PasswordError } from "../../../@types/password";
import SuccessBanner from "../../common/Banner/SuccessBanner";
import { useTranslation } from "react-i18next";
import ChangePasswordForm from "./changePassword";
import { showToast } from "../../../utils/toastUtils";

interface Props {
  submenuTab: string;
  onBack: () => void;
}

const PasswordManager: React.FC<Props> = ({ submenuTab, onBack }) => {
  const { t } = useTranslation("settings");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<PasswordError>({});
  const [message, setMessage] = useState<string>("");
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [, setValidationResults] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleCancel = () => {
    setFormData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  React.useEffect(() => {
    if (!success) return;

    const timer = setTimeout(() => {
      setSuccess(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [success]);

  const handleFormSubmit = (currentPassword: string, newPassword: string) => {
    setIsLoading(true);

    const user = userPool.getCurrentUser();
    if (!user) {
      setIsLoading(false);
      setMessage("User is not authenticated");
      return;
    }

    if (currentPassword == newPassword) {
      showToast("New password must not match the old one.", "error");
      setIsLoading(false);
      return;
    }

    const authenticationDetails = new AuthenticationDetails({
      Username: user.getUsername(),
      Password: currentPassword,
    });

    user.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        user.changePassword(currentPassword, newPassword, (error, result) => {
          setIsLoading(false);
          if (error) {
            setMessage(error.message);
          } else {
            setMessage("Password has been changed successfully!");
            setFormData({
              currentPassword: "",
              newPassword: "",
              confirmPassword: "",
            });
            setValidationResults({
              currentPassword: "",
              newPassword: "",
              confirmPassword: "",
            });
            setSuccess(true);
          }
        });
        setIsLoading(false);
      },
      onFailure: (error) => {
        setIsLoading(false);
        if (error.code === "NotAuthorizedException") {
          setValidationErrors({ currentPassword: "Incorrect Password" });
        } else {
          setValidationErrors({
            currentPassword: "Error occurred during authentication",
          });
          showToast("Authentication error occurred!", "error");
        }
        setMessage(error.message);
      },
    });
  };

  return (
    <>
      {success && (
        <SuccessBanner message={"Password has been changed successfully!"} />
      )}

      <ChangePasswordForm
        validationErrors={validationErrors}
        setValidationErrors={setValidationErrors}
        onSubmit={handleFormSubmit}
        isLoading={isLoading}
        onCancel={handleCancel}
        formData={formData}
        setFormData={setFormData}
      />
    </>
  );
};

export default PasswordManager;
